import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsAPI } from '../lib/api';
import { Avatar } from '../components/ui';
import { addDays, todayISO } from '../lib/dates';

const OVERLOAD = 3; // tasks/day considered overallocated

export default function WorkloadsScreen() {
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0); // weeks from today
  const navigate = useNavigate();

  const start = addDays(todayISO(), offset * 7);
  const end = addDays(start, 13);

  useEffect(() => {
    reportsAPI.workloads(start, end).then(({ data }) => setData(data));
  }, [start, end]);

  if (!data) return null;
  const days = Array.from({ length: 14 }, (_, i) => addDays(start, i));
  const today = todayISO();

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-lg font-semibold">Workloads</h1>
        <span className="text-xs text-pm-faint">
          {start} → {end} · open tasks by due date
        </span>
        <span className="ml-auto flex gap-1">
          <button type="button" onClick={() => setOffset(offset - 1)} className="rounded-md border border-pm-border px-2 py-1 text-xs text-pm-muted hover:text-pm-text">
            ← Prev
          </button>
          <button type="button" onClick={() => setOffset(0)} className="rounded-md border border-pm-border px-2 py-1 text-xs text-pm-muted hover:text-pm-text">
            Today
          </button>
          <button type="button" onClick={() => setOffset(offset + 1)} className="rounded-md border border-pm-border px-2 py-1 text-xs text-pm-muted hover:text-pm-text">
            Next →
          </button>
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-36 border-b border-r border-pm-border bg-pm-surface px-3 py-2 text-left font-medium text-pm-faint">
                Person
              </th>
              {days.map((d) => (
                <th
                  key={d}
                  className={`min-w-16 border-b border-pm-border px-1 py-2 text-center font-medium ${
                    d === today ? 'text-pm-accent' : 'text-pm-faint'
                  }`}
                >
                  {new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.mid}>
                <td className="sticky left-0 z-10 border-b border-r border-pm-border/60 bg-pm-surface px-3 py-2">
                  <span className="flex items-center gap-2">
                    <Avatar name={m.name} size={20} />
                    {m.name}
                  </span>
                </td>
                {days.map((d) => {
                  const items = m.days[d] || [];
                  const over = items.length > OVERLOAD;
                  return (
                    <td key={d} className={`border-b border-pm-border/40 p-0.5 align-top ${d === today ? 'bg-pm-raised/40' : ''}`}>
                      {items.length > 0 && (
                        <div
                          className="rounded px-1 py-0.5 text-center"
                          style={over ? { background: 'color-mix(in srgb, var(--pm-danger) 25%, transparent)', color: 'var(--pm-danger)' } : { background: 'var(--pm-raised)' }}
                          title={items.map((i) => i.title).join('\n')}
                        >
                          <button type="button" onClick={() => navigate(`/projects/${items[0].project_id}?task=${items[0].id}`)} className="pm-num font-semibold">
                            {items.length}
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {data.members.length === 0 && (
              <tr>
                <td colSpan={15} className="px-3 py-10 text-center text-sm text-pm-muted">
                  No assigned tasks with due dates in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {data.unassigned.length > 0 && (
        <p className="mt-3 text-xs text-pm-faint">
          <span className="pm-num">{data.unassigned.length}</span> due task(s) in range have no assignee.
        </p>
      )}
    </div>
  );
}
